import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import NegotiationService from '../../../services/NegotiationService';
import UserService from '../../../services/UserService';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import { BidSource } from '../../../_shared/types/auction';
import ROUTES from '../../Routes/constants';
import convertToCurrency from '../../../_shared/utils/convertToCurrency';
import Alert from '../../../_shared/components/Alert';
import timeouts from '../../../_shared/constants/timeouts';
import ReducerConstants from '../../../_shared/constants/reducers';
import { getBidIncrement } from '../../../_shared/models/TradeDetails';
import Bid from '../../../_shared/models/Bid';
import ProposalTipMessage from './ProposalTipMessage';
import {
  ButtonPrimary,
  ButtonSendOffer,
  Footer,
  Row,
  StayFirmWrapper,
  StyledForm,
  StyledFormItem
} from './Styles';
import ConfirmModal from '../../../_shared/components/ConfirmModal';
import { onErrorCallbackGeneric } from './NegotiationModalEvents';
import useAnalytics, {
  getSellerProperties,
  getVinBasicProperties
} from '../../../_shared/hooks/useAnalytics';
import SegmentConfig from '../../../_shared/constants/segmentConfig';
import TRToast from '../../../_shared/helpers/TRToast';
import ProposalErrorModalContainer from './ProposalErrorModalContainer';

const AddProposalForm = ({
  tradeDetails,
  tradeContext,
  negotiations,
  onAddProposal,
  isCountering,
  proxyAmount,
  isStayingFirm,
  myLatestOffer,
  highBidThreshold,
  close
}) => {
  const track = useAnalytics();
  const negotiationService = NegotiationService.get();
  const eventSource = window.location.href.includes(ROUTES.TRADE_URI)
    ? BidSource.VDP_OVERLAY
    : BidSource.LIST_VIEW;
  const userService = UserService.get();
  const [user, configurations] = useSelector(
    state => [
      state[ReducerConstants.AUTH]?.user,
      state[ReducerConstants.AUTH]?.system?.configurations
    ],
    shallowEqual
  );

  const [isProposalErrorModalOpen, setIsProposalErrorModalOpen] = useState(
    false
  );
  const [proposalErrorMessage, setProposalErrorMessage] = useState(null);
  const [proposalErrorTitle, setProposalErrorTitle] = useState(null);
  const [proposalErrorMessageValues, setProposalErrorMessageValues] = useState(
    null
  );

  const [isValid, setIsValid] = useState(true);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalLogic, setConfirmModalLogic] = useState([]);
  const [textareaLetters, setTextareaLetters] = useState(0);
  const convertToNumber = text => {
    return +text.replace(/[^0-9]/g, '');
  };
  const intl = useIntl();

  const topBid = negotiations?.topBid || tradeDetails?.topBid;
  const minAmount = topBid + getBidIncrement(topBid, configurations);
  const autoCloseConfig = {
    autoClose: timeouts.ERROR
  };

  const onOfferAmountChange = e => {
    const offer = convertToNumber(e.target.value) || null;
    e.target.value = convertToCurrency(offer);
  };

  const auctionId = tradeDetails?.auctionId;

  const refNewOffer = useRef();
  const refNewOfferComment = useRef();
  const isVdp = window.location.pathname.startsWith(ROUTES.TRADE_URI);

  const openHighBidConfirmModal = onConfirm => {
    setConfirmModalLogic([
      {
        msg: (
          <FormattedMessage
            id="modal.negotiation.highBid.message"
            values={{ amount: refNewOffer.current.value }}
          />
        ),
        confirmLabel: <FormattedMessage id="modal.negotiation.send" />,
        cancelLabel: <FormattedMessage id="modal.cancel" />,
        onCancel: () => () => {
          setIsConfirmModalOpen(false);
        },
        onConfirm: () => () => {
          onConfirm();
          setIsConfirmModalOpen(false);
        }
      }
    ]);
    setIsConfirmModalOpen(true);
  };

  const onErrorCallback = () => {
    onErrorCallbackGeneric();
    setIsWaiting(false);
  };

  const handleError = (errorCode, offer) => {
    let errorMessage = '';
    let errorTitle = '';
    switch (errorCode) {
      case 1017: {
        errorMessage = 'modal.negotiation.counter.greater.floorPrice.message';
        errorTitle = 'modal.negotiation.counter.greater.floorPrice.label';
        break;
      }
      case 1016: {
        errorMessage = 'modal.negotiation.accept.toHigh.message';
        errorTitle = 'modal.negotiation.accept.toHigh.label';
        break;
      }
      case 1015: {
        errorMessage = 'modal.negotiation.accept.toLow.message';
        errorTitle = 'modal.negotiation.accept.toLow.label';
        break;
      }
      default: {
        onErrorCallback();
        close();
        return;
      }
    }
    const name = `${tradeDetails?.year} ${tradeDetails.make.name} ${tradeDetails.model.name}`;

    const errorValues = {
      name,
      offer: convertToCurrency(offer),
      floorPrice: negotiations?.floorPrice
    };
    setProposalErrorMessage(errorMessage);
    setProposalErrorTitle(errorTitle);
    setProposalErrorMessageValues(errorValues);
    setIsProposalErrorModalOpen(true);
  };

  const submitForm = () => {
    const { value } = refNewOffer.current;
    if (value === '') {
      setIsValid(false);
      TRToast(
        <Alert
          variant="error"
          messages={[<FormattedMessage id="modal.negotiation.error.noValue" />]}
        />,
        autoCloseConfig
      );
      return;
    }
    const offer = convertToNumber(value);
    if (offer < minAmount) {
      setIsValid(false);
      TRToast(
        <Alert
          variant="error"
          messages={[
            <FormattedMessage
              id="modal.negotiation.error.lowValue"
              values={{ minAmount: convertToCurrency(minAmount) }}
            />
          ]}
        />,
        autoCloseConfig
      );
      return;
    }
    const bidIncrement = getBidIncrement(topBid, configurations);

    // if the difference offered is not a valid bidIncrement
    if (!Bid.isValidBidAmount(offer - minAmount, bidIncrement)) {
      setIsValid(false);
      TRToast(
        <Alert
          variant="error"
          messages={[
            <FormattedMessage
              id="modal.negotiation.error.wrongIncrement"
              values={{ bidIncrement: `$${bidIncrement}` }}
            />
          ]}
        />,
        autoCloseConfig
      );
      return;
    }
    const createProposal = async () => {
      const response = await negotiationService.createProposal(auctionId, {
        comment: refNewOfferComment.current.value,
        price: offer
      });
      const newProposal = response.proposal;
      if (!response || response?.errorCode || response?.errorMessage) {
        const regex = /\b\d+\b/g;
        const offers = response?.errorMessage?.match(regex);
        handleError(
          response?.errorCode,
          offers ? offers[0] : tradeDetails?.negotiationOffer
        );
      } else {
        onAddProposal(newProposal);
        setIsValid(true);
        setIsWaiting(false);

        track(SegmentConfig.names.pendingNegotiateConfirmed, {
          ...getVinBasicProperties(tradeDetails),
          ...getSellerProperties(tradeDetails),
          event_state_type: userService.isSeller(user, tradeDetails?.dealer?.id)
            ? SegmentConfig.eventStateType.sellPending
            : SegmentConfig.eventStateType.buyPending,
          nego_offer_value: offer,
          nego_offer_type: isCountering
            ? SegmentConfig.negoOfferType.counterOffer
            : SegmentConfig.negoOfferType.firstOffer
        });

        track(MixpanelConfig.names.sendNegotiation, {
          user_type: userService.isSeller(user, tradeDetails?.dealer?.id)
            ? MixpanelConfig.userType.seller
            : MixpanelConfig.userType.buyer,
          source: eventSource,
          trade_id: tradeDetails?.id,
          auction_id: tradeDetails?.auctionId,
          offer_value: offer,
          offer_type: isCountering
            ? MixpanelConfig.offerType.counterOffer
            : MixpanelConfig.offerType.firstOffer
        });
        setIsWaiting(true);

        if (isVdp) {
          setTimeout(() => window.location.reload(), 1000);
        }
        close();
      }
    };

    if (offer >= highBidThreshold) {
      openHighBidConfirmModal(createProposal);
    } else {
      createProposal();
    }
  };

  const onStayFirm = () => {
    negotiationService
      .stayFirm(auctionId, {
        comment: refNewOfferComment.current.value,
        price: myLatestOffer
      })
      .then(newProposal => {
        onAddProposal(newProposal);
        setIsValid(true);
        setIsWaiting(false);
        track(SegmentConfig.names.pendingNegotiateConfirmed, {
          ...getVinBasicProperties(tradeDetails),
          ...getSellerProperties(tradeDetails),
          event_state_type: userService.isSeller(user, tradeDetails?.dealer?.id)
            ? SegmentConfig.eventStateType.sellPending
            : SegmentConfig.eventStateType.buyPending,
          nego_offer_value: myLatestOffer,
          nego_offer_type: SegmentConfig.negoOfferType.stayFirm
        });

        track(MixpanelConfig.names.sendNegotiation, {
          user_type: userService.isSeller(user, tradeDetails?.dealer?.id)
            ? MixpanelConfig.userType.seller
            : MixpanelConfig.userType.buyer,
          source: eventSource,
          trade_id: tradeDetails?.id,
          auction_id: tradeDetails?.auctionId,
          offer_value: myLatestOffer,
          offer_type: MixpanelConfig.offerType.stayFirm
        });
      })
      .catch(onErrorCallback)
      .finally(() => {
        close();
      });
    setIsWaiting(true);

    track(SegmentConfig.names.pendingStayFirmConfirmed, {
      ...getVinBasicProperties(tradeDetails),
      ...getSellerProperties(tradeDetails),
      event_state_type: userService.isSeller(user, tradeDetails?.dealer?.id)
        ? SegmentConfig.eventStateType.sellPending
        : SegmentConfig.eventStateType.buyPending,
      nego_offer_value: myLatestOffer,
      nego_offer_type: SegmentConfig.negoOfferType.stayFirm
    });
    track(MixpanelConfig.names.stayFirm, {
      user_type: userService.isSeller(user, tradeDetails?.dealer?.id)
        ? MixpanelConfig.userType.seller
        : MixpanelConfig.userType.buyer,
      source: eventSource,
      trade_id: tradeDetails?.id,
      auction_id: tradeDetails?.auctionId
    });

    if (isVdp) {
      setTimeout(() => window.location.reload(), 1000);
    }
  };

  const updateCounter = event => {
    const value = event.target.value?.length;
    setTextareaLetters(value);
  };

  return (
    <>
      {isProposalErrorModalOpen ? (
        <ProposalErrorModalContainer
          isProposalErrorModalOpen={isProposalErrorModalOpen}
          proposalErrorMessage={proposalErrorMessage}
          proposalErrorTitle={proposalErrorTitle}
          proposalErrorMessageValues={proposalErrorMessageValues}
          setIsProposalErrorModalOpen={setIsProposalErrorModalOpen}
        />
      ) : (
        <></>
      )}
      <StyledForm disabled={isWaiting} data-testid="add-proposal-form">
        {!isStayingFirm && (
          <Row>
            <StyledFormItem>
              <StyledFormItem.Label isInvalid={!isValid}>
                <FormattedMessage
                  id={
                    isCountering
                      ? 'modal.negotiation.counterAmount'
                      : 'modal.negotiation.offerAmount'
                  }
                />
              </StyledFormItem.Label>
              <StyledFormItem.Input
                ref={refNewOffer}
                isInvalid={!isValid}
                type="text"
                placeholder={intl.formatMessage(
                  {
                    id: 'modal.negotiation.minimumBid'
                  },
                  {
                    minimumBid: convertToCurrency(minAmount)
                  }
                )}
                onChange={onOfferAmountChange}
              />
            </StyledFormItem>
            <ProposalTipMessage
              tradeContext={tradeContext}
              proxyAmount={proxyAmount}
              floorPrice={negotiations?.floorPrice}
            />
          </Row>
        )}
        <Footer>
          <StyledFormItem.TextAreaWrapper>
            <StyledFormItem.LabelWithHintWrapper>
              <StyledFormItem.Label>
                <FormattedMessage id="modal.negotiation.comment" />{' '}
                <span>
                  (<FormattedMessage id="modal.negotiation.optional" />)
                </span>
              </StyledFormItem.Label>
              <StyledFormItem.Label>
                <span>
                  <FormattedMessage
                    id="modal.negotiation.comment.max"
                    values={{ value: 240 - textareaLetters }}
                  />
                </span>
              </StyledFormItem.Label>
            </StyledFormItem.LabelWithHintWrapper>
            <StyledFormItem.TextArea
              maxLength={240}
              placeholder={intl.formatMessage({
                id: 'modal.negotiation.comment.placeholder'
              })}
              ref={refNewOfferComment}
              onChange={updateCounter}
            />
          </StyledFormItem.TextAreaWrapper>
          {!isStayingFirm && (
            <ButtonSendOffer onClick={submitForm}>
              <FormattedMessage
                id={
                  isWaiting
                    ? 'modal.negotiation.loading'
                    : 'modal.negotiation.send'
                }
              />
            </ButtonSendOffer>
          )}
        </Footer>
        {isStayingFirm && (
          <StayFirmWrapper>
            <ButtonPrimary onClick={onStayFirm} disabled={isWaiting}>
              <FormattedMessage
                id={
                  isWaiting
                    ? 'modal.negotiation.loading'
                    : 'modal.negotiation.stayFirm.stayFirmButton'
                }
                values={{ offer: convertToCurrency(myLatestOffer) }}
              />
            </ButtonPrimary>
          </StayFirmWrapper>
        )}
        <ConfirmModal
          modalLogic={confirmModalLogic}
          isOpen={isConfirmModalOpen}
        />
      </StyledForm>
    </>
  );
};

export default AddProposalForm;
